var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable @typescript-eslint/no-floating-promises */
/* istanbul ignore file */
import { BroadcastType, createPennyBroadcastSubscriber } from '../utils/pennyBroadcast';
import analytics from '../analytics/analytics';
import fetchWithTimeout from '../utils/fetchWithTimeout';
export default class SpeechFetcher {
  constructor(speechUrl = 'https://www.priceline.com/genai-svc/genai/audio/speech', audioPlayer, timeout = 10000) {
    this.currentAudio = null;
    this.textQueue = [];
    this.audioQueue = [];
    this.audioPlayer = audioPlayer;
    this.isFetching = false;
    this.isPlaying = false;
    this.isDestroying = false;
    this.timeout = timeout;
    this.speechUrl = speechUrl;
    this.fullTranscript = '';
    this.broadcastChannel = createPennyBroadcastSubscriber();
  }
  destroy() {
    this.isDestroying = true;
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    this.broadcastChannel = null;
    if (this.currentAudio) {
      this.currentAudio.pause();
      URL.revokeObjectURL(this.currentAudio.src); // Clean up the object URL
      this.currentAudio = null;
    }
  }
  addToQueue(text, messageId) {
    this.textQueue.push({
      text,
      messageId
    });
    if (!this.isFetching) {
      this.processQueue();
    }
    this.fullTranscript += text;
  }
  processQueue() {
    return __awaiter(this, void 0, void 0, function* () {
      if (this.isDestroying) {
        return;
      }
      if (this.textQueue.length === 0) {
        this.isFetching = false;
        if (this.audioQueue.length > 0 && !this.isPlaying) {
          this.playNextAudio();
        }
        return;
      }
      this.isFetching = true;
      const textToSynthesize = this.textQueue.shift();
      if (!textToSynthesize) {
        return;
      }
      try {
        const audioBlob = yield this.fetchSpeech(textToSynthesize);
        this.audioQueue.push({
          text: textToSynthesize.text,
          messageId: textToSynthesize.messageId,
          audioBlob
        });
        if (!this.isPlaying) {
          this.playNextAudio();
        }
      } catch (error) {
        analytics.logError({
          message: 'Failed to fetch speech. Halting speech playback and falling back to text rendering.',
          error: JSON.stringify(error)
        });
        this.stopPlaybackAndClearQueue();
      } finally {
        this.isFetching = false;
        this.processQueue();
      }
    });
  }
  fetchSpeech(message) {
    return __awaiter(this, void 0, void 0, function* () {
      if (!this.broadcastChannel) {
        throw new Error('Broadcast channel is not available');
      }
      this.broadcastChannel.postMessage({
        type: BroadcastType.AUDIO_RETRIEVAL_INITIATED,
        data: message.text,
        sender: 'SpeechFetcher'
      });
      const speechResponse = yield fetchWithTimeout(this.speechUrl, {
        method: 'POST',
        body: JSON.stringify({
          text: message.text,
          useCustomVoice: true
        }),
        headers: {
          'Content-Type': 'application/json',
          accept: 'audio/mpeg'
        },
        timeout: this.timeout
      });
      if (!speechResponse.ok) {
        throw new Error(`Speech fetch failed: ${speechResponse.statusText}`);
      }
      return yield speechResponse.blob();
    });
  }
  playNextAudio() {
    return __awaiter(this, void 0, void 0, function* () {
      // If the broadcast channel is null, that means we shouldn't be playing audio
      if (!this.broadcastChannel) {
        return;
      }
      if (this.audioQueue.length === 0 && !this.isPlaying) {
        setTimeout(() => {
          if (!this.isPlaying && !this.isDestroying) {
            // Audio playback ended and nothing started playing for 2 seconds, we can assume
            // there is no more audio to play
            if (this.broadcastChannel) {
              this.broadcastChannel.postMessage({
                type: BroadcastType.AUDIO_QUEUE_DRAINED,
                sender: 'SpeechFetcher.playNextAudio().timeout()'
              });
            }
            this.isPlaying = false;
          }
        }, 2000);
        return;
      }
      const audioData = this.audioQueue.shift();
      // If we have audio data and the tab containing penny is active, play the audio
      if (audioData && this.audioPlayer && !document.hidden) {
        this.audioPlayer.src = URL.createObjectURL(audioData.audioBlob);
        this.currentAudio = this.audioPlayer; // Update the current audio reference
        this.isPlaying = true;
        if (this.broadcastChannel) {
          this.broadcastChannel.postMessage({
            type: BroadcastType.AUDIO_PLAYBACK_STARTED,
            data: audioData.text,
            sender: 'SpeechFetcher.playNextAudio()'
          });
        }
      }
      return new Promise(resolve => {
        this.audioPlayer.onended = () => {
          // set is playing to false in context
          URL.revokeObjectURL(this.audioPlayer.src);
          this.isPlaying = false;
          if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({
              type: BroadcastType.AUDIO_PLAYBACK_STOPPED,
              hasMoreMessages: !!(this.audioQueue.length || this.textQueue.length),
              sender: 'SpeechFetcher.playNextAudio().onended()'
            });
          }
          this.currentAudio = null; // Reset the current audio reference
          this.playNextAudio();
          resolve();
        };
        this.audioPlayer.onerror = () => {
          this.isPlaying = false;
          if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({
              type: BroadcastType.AUDIO_PLAYBACK_STOPPED,
              hasMoreMessages: !!(this.audioQueue.length || this.textQueue.length),
              sender: 'SpeechFetcher.playNextAudio().onerror()'
            });
          }
          this.currentAudio = null; // Reset on error as well
          analytics.logError({
            message: 'Audio playback failed'
          });
        };
        this.audioPlayer.play().catch(error => {
          analytics.logError({
            message: 'Audio playback failed',
            error: JSON.stringify(error)
          });
          this.stopPlaybackAndClearQueue();
        });
      });
    });
  }
  stopPlaybackAndClearQueue() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      URL.revokeObjectURL(this.currentAudio.src); // Clean up the object URL
      this.currentAudio = null;
    }
    // Clear both the audio and text queues
    this.audioQueue.length = 0;
    this.textQueue.length = 0;
    this.audioQueue = [];
    this.textQueue = [];
    // Reset playback and fetching flags
    this.isPlaying = false;
    this.isFetching = false;
    if (this.broadcastChannel) {
      // Broadcast that the queue has been drained (since we're not going to play the audio) and
      // the UI can clean itself up in response.
      this.broadcastChannel.postMessage({
        type: BroadcastType.AUDIO_QUEUE_DRAINED,
        sender: 'SpeechFetcher'
      });
      // Trigger the audio end callback to ensure consistent state and callbacks
      this.broadcastChannel.postMessage({
        type: BroadcastType.AUDIO_PLAYBACK_STOPPED,
        hasMoreMessages: !!(this.audioQueue.length || this.textQueue.length),
        sender: 'SpeechFetcher.stopPlaybackAndClearQueue()'
      });
    }
  }
}